import { useGetChecklistByQueueIdQuery, useUpdateChecklistItemMutation } from '@/services/checklists';
import { toast } from '@palmetto/palmetto-components';
import { Formik } from 'formik';
import React, { useCallback, useState } from 'react';

import { ChecklistItemStatus, QueueStatus } from 'types';
import type { ChecklistPendingFlagDecision, EvaluationState } from 'types';

import { QueueFlagsProvider } from '../QueueFlags/QueueFlagsContext';

import { ChecklistProvider } from './ChecklistContext';
import { ChecklistPanel } from './ChecklistPanel';
import { ChecklistTooltipProvider } from './ChecklistTooltipDock';

export interface ChecklistDrawerContentProps {
  queueId: string;
  onFlagFlowChange?: (active: boolean) => void;
  onCompleteReview?: (targetStatus?: QueueStatus) => Promise<void>;
}

export const ChecklistDrawerContent: React.FC<ChecklistDrawerContentProps> = ({
  queueId,
  onFlagFlowChange,
  onCompleteReview,
}) => {
  const { data: checklist, isLoading, error } = useGetChecklistByQueueIdQuery(queueId);
  const [updateItem] = useUpdateChecklistItemMutation();
  const [updatingItemKey, setUpdatingItemKey] = useState<string | null>(null);

  const handleUpdateItem = useCallback(
    async (
      itemKey: string,
      status: ChecklistItemStatus,
      data: Record<string, string | boolean | ChecklistPendingFlagDecision[]>,
      evaluations?: Record<string, EvaluationState>,
    ) => {
      if (!checklist) return;
      setUpdatingItemKey(itemKey);
      try {
        await updateItem({
          checklistId: checklist.id,
          itemKey,
          status,
          ...(Object.keys(data).length > 0 && { data }),
          ...(evaluations && Object.keys(evaluations).length > 0 && { evaluations }),
        }).unwrap();
      } catch {
        toast.error('Failed to update checklist item');
      } finally {
        setUpdatingItemKey(null);
      }
    },
    [checklist, updateItem],
  );

  if (isLoading) {
    return <p className="p-4 text-sm text-text-secondary">Loading checklist...</p>;
  }

  if (error || !checklist) {
    return null;
  }

  return (
    <Formik initialValues={{}} onSubmit={async () => {}}>
      <ChecklistProvider checklistId={checklist.id} accountId={checklist.accountId} queueId={queueId}>
        <QueueFlagsProvider queueId={queueId}>
          <ChecklistTooltipProvider key={checklist.id}>
            <ChecklistPanel
              checklist={checklist}
              onUpdateItem={handleUpdateItem}
              updatingItemKey={updatingItemKey}
              queueId={queueId}
              onFlagFlowChange={onFlagFlowChange}
              onCompleteReview={onCompleteReview}
            />
          </ChecklistTooltipProvider>
        </QueueFlagsProvider>
      </ChecklistProvider>
    </Formik>
  );
};
